import type { ActionId, PlayerInput } from '@cratered/shared';
import type { AimPreview } from './render';
import { clamp } from './rng';
import { type Phase, UNIT_H, UNIT_HALF_W } from './sim';
import { ACTIONS, MENU_ORDER } from './weapons';

export interface InputHost {
  phase(): Phase;
  /** Brawl's live phase: no turns, the local mole walks, aims and fires whenever its cooldown allows. */
  live(): boolean;
  /** Is it the local player's turn and are they allowed to act right now? */
  canAct(): boolean;
  action(): ActionId | null;
  activeUnit(): { id: string; x: number; y: number; facing: 1 | -1; aim: number } | null;
  ownUnits(): { id: string; x: number; y: number }[];
  send(input: PlayerInput): void;
  screenToWorld(px: number, py: number): { x: number; y: number };
  preview(aim: AimPreview | null): void;
  pointer(world: { x: number; y: number } | null): void;
  /** The HUD menu: pick an action by number key. */
  menuPick(index: number): void;
}

const MAX_DRAG = 260;

/**
 * Pointer: in the select phase click one of your moles; while aiming, press, drag to set
 * angle and power (Raft Wars style), release to throw; with the gun the crosshair follows the
 * pointer and a click fires. Keyboard mirrors Territory War: arrows move / aim, space fires.
 *
 * Brawl (the live phase) folds that into one: a drag from anywhere aims and throws what is in
 * hand, a click shoots a gun at the pointer, left / right walk while held, up jumps, space fires
 * with the current aim, the digits pick a weapon and E boots. The aiming keys are not used
 * there, since the pointer (or a stick) aims.
 */
export class InputController {
  private angle = 0.6;
  private power = 0.6;
  private dragging = false;
  private pointerId: number | null = null;
  private moveDir: -1 | 0 | 1 = 0;
  private disposed = false;

  constructor(private readonly canvas: HTMLCanvasElement, private readonly host: InputHost) {
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    canvas.addEventListener('pointerleave', this.onPointerLeave);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  dispose(): void {
    this.disposed = true;
    // A key still held when the match screen goes must not leave the mole walking.
    this.setMove(0);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('pointerleave', this.onPointerLeave);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  /** Reset per turn (and, in a brawl, as the live phase starts and ends) so a new mole doesn't inherit a half-set shot or a held walk key. */
  resetTurn(): void {
    this.dragging = false;
    this.setMove(0);
    const u = this.host.activeUnit();
    this.angle = u?.aim ?? 0.6;
    this.power = 0.6;
    this.host.preview(null);
  }

  /** On-screen move pad hooks (mobile). */
  move(dir: -1 | 0 | 1): void {
    this.setMove(dir);
  }

  jump(): void {
    if (this.host.phase() === 'move' || this.host.live()) this.host.send({ kind: 'jump' });
  }

  /** A controller stick: aim at `angle` with `power`, facing the way the stick points. */
  setAim(angle: number, power: number, facing: 1 | -1): void {
    const u = this.host.activeUnit();
    if (!u || !this.aiming() || !this.host.canAct()) return;
    if (facing !== u.facing) this.turnTo(facing);
    this.angle = clamp(angle, -Math.PI / 2, Math.PI / 2);
    this.power = clamp(power, 0.12, 1);
    this.pushAim();
  }

  /** A controller button (or space): let the shot go with the current aim. In a brawl a melee weapon or gear in hand is used again instead. */
  fire(): void {
    if (!this.aiming() || !this.host.canAct()) return;
    const kind = this.aimKind();
    if (kind === 'throw') this.host.send({ kind: 'fire', angle: this.angle, power: this.power });
    else if (kind === 'gun') this.host.send({ kind: 'shoot', angle: this.angle });
    else if (this.host.live()) {
      const a = this.host.action();
      if (a) this.host.send({ kind: 'action', action: a });
    }
    this.host.preview(null);
  }

  /** Whether the current aim can be set and fired: the aim phase, or any moment of a brawl's live phase. */
  private aiming(): boolean {
    return this.host.phase() === 'aim' || this.host.live();
  }

  /**
   * Face `facing` without ending up walking. The sim's live 'move' both turns and walks, so the
   * turn is followed by the walk state the keys already hold (0 when none is down).
   */
  private turnTo(facing: 1 | -1): void {
    this.host.send({ kind: 'move', dir: facing });
    if (this.host.live() && this.moveDir !== facing) this.host.send({ kind: 'move', dir: this.moveDir });
  }

  private aimKind(): 'throw' | 'gun' | null {
    const a = this.host.action();
    if (!a) return null;
    const k = ACTIONS[a].kind;
    return k === 'throw' ? 'throw' : k === 'hitscan' ? 'gun' : null;
  }

  private face(worldX: number): void {
    const u = this.host.activeUnit();
    if (!u) return;
    if (Math.abs(worldX - u.x) > 14 && Math.sign(worldX - u.x) !== u.facing) {
      this.turnTo(worldX > u.x ? 1 : -1);
    }
  }

  private aimFrom(clientX: number, clientY: number): void {
    const u = this.host.activeUnit();
    if (!u) return;
    const w = this.host.screenToWorld(clientX, clientY);
    const ox = u.x;
    const oy = u.y - UNIT_H * 0.7;
    const dxRaw = w.x - ox;
    const dy = oy - w.y;
    const facing: 1 | -1 = Math.abs(dxRaw) > 14 ? (dxRaw > 0 ? 1 : -1) : u.facing;
    const dx = Math.abs(dxRaw);
    this.angle = clamp(Math.atan2(dy, Math.max(6, dx)), -Math.PI / 2, Math.PI / 2);
    this.power = clamp(Math.hypot(dxRaw, dy) / MAX_DRAG, 0.12, 1);
    if (facing !== u.facing) this.turnTo(facing);
    const kind = this.aimKind();
    if (kind) this.host.preview({ unitId: u.id, angle: this.angle, power: this.power, kind });
    this.host.send({ kind: 'aim', angle: this.angle, power: this.power });
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    (e.target as HTMLElement).focus?.();
    if (!this.host.canAct()) return;
    const phase = this.host.phase();
    const w = this.host.screenToWorld(e.clientX, e.clientY);
    if (this.host.live()) {
      // Brawl: a click shoots the gun in hand at the pointer; a drag from anywhere aims a throw.
      const kind = this.aimKind();
      if (kind === 'gun') {
        this.aimFrom(e.clientX, e.clientY);
        this.host.send({ kind: 'shoot', angle: this.angle });
        this.host.preview(null);
      } else if (kind === 'throw') {
        this.dragging = true;
        this.pointerId = e.pointerId;
        this.canvas.setPointerCapture(e.pointerId);
        this.aimFrom(e.clientX, e.clientY);
      } else this.face(w.x);
      return;
    }
    if (phase === 'select') {
      // Click one of your moles to make it the active one.
      const hit = this.host.ownUnits().find((u) => Math.abs(w.x - u.x) <= UNIT_HALF_W + 8 && w.y <= u.y + 6 && w.y >= u.y - UNIT_H - 10);
      if (hit) this.host.send({ kind: 'select', unitId: hit.id });
      else this.face(w.x);
      return;
    }
    if (phase === 'move') {
      // Tap left/right of the mole to walk that way; release to stop.
      const u = this.host.activeUnit();
      if (u) this.setMove(w.x < u.x ? -1 : 1);
      this.pointerId = e.pointerId;
      this.canvas.setPointerCapture(e.pointerId);
      return;
    }
    if (phase === 'aim') {
      const kind = this.aimKind();
      if (kind === 'gun') {
        this.aimFrom(e.clientX, e.clientY);
        this.host.send({ kind: 'shoot', angle: this.angle });
        this.host.preview(null);
        return;
      }
      if (kind === 'throw') {
        this.dragging = true;
        this.pointerId = e.pointerId;
        this.canvas.setPointerCapture(e.pointerId);
        this.aimFrom(e.clientX, e.clientY);
      }
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.host.canAct()) {
      this.host.pointer(null);
      return;
    }
    const w = this.host.screenToWorld(e.clientX, e.clientY);
    const phase = this.host.phase();
    if (phase === 'aim' || this.host.live()) {
      const kind = this.aimKind();
      if (kind === 'gun') {
        this.host.pointer(w);
        this.aimFrom(e.clientX, e.clientY);
        return;
      }
      if (kind === 'throw' && this.dragging) this.aimFrom(e.clientX, e.clientY);
      return;
    }
    if (phase === 'move' && this.pointerId === e.pointerId) {
      const u = this.host.activeUnit();
      if (u) this.setMove(w.x < u.x ? -1 : 1);
      return;
    }
    this.host.pointer(null);
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (this.pointerId !== null && e.pointerId !== this.pointerId) return;
    this.pointerId = null;
    if (this.host.phase() === 'move') {
      this.setMove(0);
      return;
    }
    if (this.dragging) {
      this.dragging = false;
      if (this.host.canAct() && this.aimKind() === 'throw') {
        this.host.send({ kind: 'fire', angle: this.angle, power: this.power });
        this.host.preview(null);
      }
    }
  };

  private onPointerLeave = (): void => {
    this.host.pointer(null);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (!this.host.canAct()) return;
    const phase = this.host.phase();
    const u = this.host.activeUnit();
    if (this.host.live()) {
      this.onLiveKeyDown(e);
      return;
    }
    switch (e.code) {
      case 'ArrowLeft':
      case 'KeyA':
        if (phase === 'move') this.setMove(-1);
        else if (phase === 'aim' && this.aimKind() === 'throw') this.bumpPower(-0.04);
        else this.host.send({ kind: 'move', dir: -1 });
        e.preventDefault();
        break;
      case 'ArrowRight':
      case 'KeyD':
        if (phase === 'move') this.setMove(1);
        else if (phase === 'aim' && this.aimKind() === 'throw') this.bumpPower(0.04);
        else this.host.send({ kind: 'move', dir: 1 });
        e.preventDefault();
        break;
      case 'ArrowUp':
      case 'KeyW':
        if (phase === 'move') {
          if (!e.repeat) this.host.send({ kind: 'jump' });
        } else if (phase === 'aim') this.bumpAngle(0.04);
        e.preventDefault();
        break;
      case 'ArrowDown':
      case 'KeyS':
        if (phase === 'aim') this.bumpAngle(-0.04);
        e.preventDefault();
        break;
      case 'Space':
      case 'Enter':
        if (e.repeat) break;
        if (phase === 'aim') {
          const kind = this.aimKind();
          if (kind === 'throw') this.host.send({ kind: 'fire', angle: this.angle, power: this.power });
          else if (kind === 'gun') this.host.send({ kind: 'shoot', angle: this.angle });
          this.host.preview(null);
        } else if (phase === 'move') {
          this.host.send({ kind: 'stopMove' });
        }
        e.preventDefault();
        break;
      case 'Escape':
        if (phase === 'aim') this.host.send({ kind: 'cancel' });
        else if (phase === 'move') this.host.send({ kind: 'stopMove' });
        break;
      case 'Tab': {
        if (phase !== 'select') break;
        const own = this.host.ownUnits();
        const i = own.findIndex((x) => x.id === u?.id);
        const next = own[(i + 1) % own.length];
        if (next) this.host.send({ kind: 'select', unitId: next.id });
        e.preventDefault();
        break;
      }
      default: {
        const n = /^Digit([1-9])$/.exec(e.code);
        if (n && phase === 'select') this.host.menuPick(Number(n[1]) - 1);
      }
    }
  };

  /** Brawl keys. Walking is sent once per press and release (setMove drops repeats), never per key repeat. */
  private onLiveKeyDown(e: KeyboardEvent): void {
    switch (e.code) {
      case 'ArrowLeft':
      case 'KeyA':
        this.setMove(-1);
        e.preventDefault();
        break;
      case 'ArrowRight':
      case 'KeyD':
        this.setMove(1);
        e.preventDefault();
        break;
      case 'ArrowUp':
      case 'KeyW':
        if (!e.repeat) this.host.send({ kind: 'jump' });
        e.preventDefault();
        break;
      case 'Space':
      case 'Enter':
        if (!e.repeat) this.fire();
        e.preventDefault();
        break;
      case 'KeyE':
        if (!e.repeat) this.host.send({ kind: 'kick' });
        break;
      default: {
        const n = /^Digit([1-9])$/.exec(e.code);
        if (n && !e.repeat) this.host.menuPick(Number(n[1]) - 1);
      }
    }
  }

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
      if (this.moveDir === -1) this.setMove(0);
    } else if (e.code === 'ArrowRight' || e.code === 'KeyD') {
      if (this.moveDir === 1) this.setMove(0);
    }
  };

  private bumpAngle(d: number): void {
    this.angle = clamp(this.angle + d, -Math.PI / 2, Math.PI / 2);
    this.pushAim();
  }

  private bumpPower(d: number): void {
    this.power = clamp(this.power + d, 0.12, 1);
    this.pushAim();
  }

  private pushAim(): void {
    const u = this.host.activeUnit();
    const kind = this.aimKind();
    if (u && kind) this.host.preview({ unitId: u.id, angle: this.angle, power: this.power, kind });
    this.host.send({ kind: 'aim', angle: this.angle, power: this.power });
  }

  private setMove(dir: -1 | 0 | 1): void {
    if (this.moveDir === dir) return;
    this.moveDir = dir;
    this.host.send({ kind: 'move', dir });
  }
}

export { MENU_ORDER };
